import { useEffect, useMemo, useState } from 'react';
import { Check, Copy, Loader2, MoreVertical } from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ConnectorIcon } from '@/components/connectors/ConnectorIcon';
import { Connection, ConnectionStatus, ConnectionType, ConnectorListItem } from '@optidev/shared-types';
import { cn } from '@/lib/utils';

interface ConnectionsListProps {
  connections: Connection[];
  connectors?: ConnectorListItem[];
  onDelete: (id: string) => void;
  onTest: (id: string) => void;
  onRefreshToken?: (id: string) => void;
  onToggleEnabled?: (id: string, enabled: boolean) => void;
  onViewUsage?: (connection: Connection) => void;
}

/**
 * One grade down from the default menu row, so the menu sits under the card rather than over it.
 * No leading icons: five of them turned a list of five short words into a busy little toolbar,
 * and none of the words needed illustrating. An in-flight action gets a trailing spinner, which
 * keeps the labels still.
 */
const MENU_ITEM = 'px-2 py-1.5 text-[12.5px]';
const MENU_SPINNER = 'ml-auto size-3 flex-none animate-spin opacity-60';

/** Fixed card footprint, shared by a connection card and a folder so the row stays even. */
const CARD_WIDTH = 215;
const CARD_GAP = 12; // gap-3
const CARD_SHELL = 'group w-[215px] flex-none rounded-2xl border transition-shadow hover:shadow-md';
const CARD_BODY = 'flex h-full min-h-[152px] flex-col p-3.5';

/** Below this, a connector's connections stay as individual cards rather than folding. */
const MIN_FOR_FOLDER = 2;

/** Rows of names a folder previews before the last one turns into a count of the rest. */
const FOLDER_PREVIEW_ROWS = 3;

/** Cards per row in an opened folder; the dialog is sized to exactly this many. */
const FOLDER_COLUMNS = 3;

/**
 * Readable text colour for a solid brand background.
 *
 * `brandTheme.primaryForeground` is honoured when a connector sets it, but it is derived here as
 * well: the cards are painted with 41 different brand colours, several of which (Mailchimp's yellow,
 * Airtable's amber) would fail badly with white text, and one missing field should not produce an
 * unreadable card.
 */
function readableOn(hex: string, override?: string): string {
  if (override) return override;
  const v = hex.replace('#', '');
  if (v.length !== 6) return '#ffffff';
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(v.slice(i, i + 2), 16));
  const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return luminance > 0.62 ? '#17151f' : '#ffffff';
}

// Parse connection name to extract display name and account email
// e.g., "Google Sheets (email@example.com) #2" -> { displayName: "Google Sheets #2", account: "email@example.com" }
const parseConnectionName = (name: string): { displayName: string; account: string | null } => {
  // Match email in parentheses: (something@something.com)
  const emailMatch = name.match(/\(([^)]+@[^)]+)\)/);

  if (emailMatch) {
    const account = emailMatch[1];
    // Remove the (email) part and clean up extra spaces
    const displayName = name.replace(emailMatch[0], '').replace(/\s+/g, ' ').trim();
    return { displayName, account };
  }

  return { displayName: name, account: null };
};

export default function ConnectionsList({
  connections,
  connectors = [],
  onDelete,
  onTest,
  onRefreshToken,
  onToggleEnabled,
  onViewUsage,
}: ConnectionsListProps) {
  // Create a map of connector ID -> connector info for quick lookup
  const connectorsMap = new Map(connectors.map(c => [c.id, c]));

  const [testingId, setTestingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleTest = async (id: string) => {
    setTestingId(id);
    try {
      await onTest(id);
    } finally {
      setTestingId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    setDeletingId(deleteConfirmId);
    try {
      await onDelete(deleteConfirmId);
    } finally {
      setDeletingId(null);
      setDeleteConfirmId(null);
    }
  };

  const handleRefreshToken = async (id: string) => {
    if (!onRefreshToken) return;
    setRefreshingId(id);
    try {
      await onRefreshToken(id);
    } finally {
      setRefreshingId(null);
    }
  };

  const handleToggleEnabled = async (id: string, currentEnabled: boolean) => {
    if (!onToggleEnabled) return;
    setTogglingId(id);
    try {
      await onToggleEnabled(id, !currentEnabled);
      // Matches the card's status pill, which reads "Paused" rather than "Disabled".
      toast.success(currentEnabled ? 'Connection paused' : 'Connection resumed');
    } catch {
      toast.error('Failed to update connection');
    } finally {
      setTogglingId(null);
    }
  };

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyId = (id: string) => {
    navigator.clipboard.writeText(id);
    setCopiedId(id);
    setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 2000);
    toast.success('Connection ID copied to clipboard');
  };

  // Get connection name for delete confirmation
  const connectionToDelete = deleteConfirmId
    ? connections.find(c => c.id === deleteConfirmId)
    : null;

  // Only user OAuth connections (not service accounts/machine credentials)
  const machineAuthMethods = ['service_account', 'service_principal', 'client_credentials'];
  // OAuth or machine credentials (for refresh token functionality)
  const isOAuthConnection = (connection: Connection): boolean => {
    return connection.authType === ConnectionType.OAUTH ||
           connection.authType === ConnectionType.OAUTH_MACHINE ||
           (connection.authMethod?.startsWith('oauth') ?? false);
  };

  // Being an OAuth connection is not enough to refresh: some providers issue
  // non-expiring tokens with no refresh token (GitHub OAuth Apps, Slack), and a
  // static-token auth method (PAT / API key) on an OAuth connector never has one.
  // The API reports this per connection; offering the action anyway just surfaces
  // "no refresh token available". Treat a missing flag as refreshable so an older
  // API response doesn't hide a working action.
  const canRefreshToken = (connection: Connection): boolean =>
    connection.hasRefreshToken !== false;

  /**
   * Connections of the same service fold into one folder, iOS-home-screen style.
   *
   * Four Stripe connections used to be four near-identical cards you had to read the small line on
   * to tell apart, and they pushed everything else out of the row. Grouped, the row says which
   * services you have connected — which is the question it is there to answer — and the folder
   * opens to the connections themselves, rendered by the same card, so nothing is only reachable
   * from one place. Order follows first appearance, so the row doesn't reshuffle as you connect.
   */
  const groups = useMemo(() => {
    const byConnector = new Map<string, Connection[]>();
    for (const connection of connections) {
      const key = connection.type || 'unknown';
      const existing = byConnector.get(key);
      if (existing) existing.push(connection);
      else byConnector.set(key, [connection]);
    }
    return [...byConnector.entries()].map(([type, items]) => ({ type, items }));
  }, [connections]);

  const [openFolderType, setOpenFolderType] = useState<string | null>(null);
  const openFolder = openFolderType
    ? groups.find((group) => group.type === openFolderType)
    : undefined;
  /** Two connections get a two-column dialog, not a three-column one with a hole in it. */
  const folderColumns = Math.min(openFolder?.items.length ?? 1, FOLDER_COLUMNS);

  /*
   * Deleting down to a single connection dissolves the folder — the open dialog would otherwise be
   * showing a group that no longer exists on the row behind it.
   */
  useEffect(() => {
    if (openFolderType && (openFolder?.items.length ?? 0) < MIN_FOR_FOLDER) {
      setOpenFolderType(null);
    }
  }, [openFolderType, openFolder]);

  if (connections.length === 0) {
    return null;
  }

  const renderCard = (connection: Connection): React.ReactElement => {
          const isOAuth = isOAuthConnection(connection);
          const isLoading = testingId === connection.id || deletingId === connection.id || refreshingId === connection.id || togglingId === connection.id;
          const isEnabled = connection.enabled !== false;
          const connectorInfo = connectorsMap.get(connection.type);
          const { displayName, account } = parseConnectionName(connection.name);
          const brand = connectorInfo?.brandTheme?.primary;
          // A disabled connection drops the colour entirely: "switched off" has to read
          // differently from "working", and a full-strength brand wash reads as healthy.
          const paint = isEnabled && brand ? brand : undefined;
          const fg = paint
            ? readableOn(paint, connectorInfo?.brandTheme?.primaryForeground)
            : undefined;

          return (
            <Card
              key={connection.id}
              data-testid="connection-card"
              data-connection-id={connection.id}
              style={paint ? { backgroundColor: paint, borderColor: paint, color: fg } : undefined}
              className={cn(
                // Fixed width so every card is the same size whatever the row holds, and so a
                // partially visible card at the edge signals that the row scrolls.
                CARD_SHELL,
                !paint && 'bg-muted/40',
              )}
            >
              {/* min-height plus a bottom-pinned meta row: at ~215px wide this lands near square,
                  and every card is the same height whatever its content. */}
              <CardContent className={CARD_BODY}>
                {/* The mark gets its own line, on white so it stays legible on any brand colour. */}
                <div className="flex items-center gap-2">
                  <span className="grid size-9 flex-none place-items-center rounded-xl bg-white p-1.5 shadow-sm">
                    <ConnectorIcon
                      connector={connectorInfo || {
                        icon: 'Database',
                        name: connection.type || 'Unknown',
                      }}
                      className="size-full"
                      // The tile is white in both themes, so the mark must be too — see the prop.
                      surface="light"
                    />
                  </span>
                  {/*
                    Which service this is, beside its mark. The line below is the connection's own
                    name, which the user chose and often does not name the service.
                  */}
                  <span className="min-w-0 flex-1 truncate text-[11.5px] font-semibold uppercase tracking-wide opacity-80">
                    {connectorInfo?.name ?? connection.type}
                  </span>

                  {/*
                    `modal={false}`: a modal Radix menu puts `pointer-events: none` on <body> while
                    it is open and takes it off on close. Nested inside the folder dialog — itself a
                    modal — the two unwind out of order and the style is left behind, which locks the
                    whole page until a reload. A small action menu needs neither the scroll lock nor
                    the focus trap, so it opts out and the conflict cannot arise.
                  */}
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="sm"
                        variant="ghost"
                        /*
                          Deliberately NOT disabled while an action is in flight. Radix hands focus
                          back to the trigger as it closes; a trigger that went disabled mid-close
                          can't take it, which strands the dismiss cleanup. The in-flight row shows
                          its own spinner, and each item disables itself.
                        */
                        onClick={(e) => e.stopPropagation()}
                        style={paint ? { color: fg } : undefined}
                        className="h-7 w-7 flex-shrink-0 p-0 hover:bg-black/10"
                      >
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    {/*
                      Actions you take *on* a connection, and nothing else: "Copy connection ID"
                      is a button on the card and editing is the card body itself.

                      Sentence case and one grade smaller than the default menu — at 14px with
                      16px icons and "Test Connection" spelled out, five rows of it dominated the
                      card it belonged to. An in-flight action swaps its own icon for a spinner
                      rather than rewriting its label, so nothing reflows mid-click.
                    */}
                    <DropdownMenuContent align="end" className="w-[184px] p-1">
                      <DropdownMenuLabel className="truncate px-2 py-1.5 text-[11px] font-medium text-muted-foreground">
                        {displayName}
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />

                      <DropdownMenuItem
                        onClick={() => handleTest(connection.id)}
                        disabled={isLoading || !isEnabled}
                        className={MENU_ITEM}
                      >
                        Test
                        {testingId === connection.id && <Loader2 className={MENU_SPINNER} />}
                      </DropdownMenuItem>

                      {onViewUsage && (
                        <DropdownMenuItem
                          onClick={() => onViewUsage(connection)}
                          className={MENU_ITEM}
                        >
                          How to use
                        </DropdownMenuItem>
                      )}

                      {isOAuth && onRefreshToken && canRefreshToken(connection) && (
                        <DropdownMenuItem
                          onClick={() => handleRefreshToken(connection.id)}
                          disabled={refreshingId === connection.id || !isEnabled}
                          className={MENU_ITEM}
                        >
                          Refresh token
                          {refreshingId === connection.id && <Loader2 className={MENU_SPINNER} />}
                        </DropdownMenuItem>
                      )}

                      {onToggleEnabled && (
                        <DropdownMenuItem
                          onClick={() => handleToggleEnabled(connection.id, isEnabled)}
                          disabled={togglingId === connection.id}
                          className={MENU_ITEM}
                        >
                          {isEnabled ? 'Pause' : 'Resume'}
                          {togglingId === connection.id && <Loader2 className={MENU_SPINNER} />}
                        </DropdownMenuItem>
                      )}

                      <DropdownMenuSeparator />

                      <DropdownMenuItem
                        onClick={() => setDeleteConfirmId(connection.id)}
                        disabled={deletingId === connection.id}
                        className={cn(
                          MENU_ITEM,
                          'text-destructive focus:bg-destructive/10 focus:text-destructive',
                        )}
                      >
                        Delete
                        {deletingId === connection.id && <Loader2 className={MENU_SPINNER} />}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className="mt-3 min-w-0">
                  <span className="block truncate text-[13.5px] font-semibold tracking-tight">
                    {displayName}
                  </span>
                  {account && (
                    <span className="mt-0.5 block truncate text-[11px] opacity-70">{account}</span>
                  )}
                </div>

                {/*
                  A single meta row that SWAPS rather than stacks: status, then either the error or
                  what is using it. An errored card is exactly as tall as a healthy one, so a grid of
                  them lines up. Connector type, created date, the OAuth badge and the project names
                  moved into the menu and the usage view, which already show them.
                */}
                <div className="mt-auto flex items-center gap-2 pt-3">
                  {/*
                    Status keeps its own semantic colour rather than borrowing the brand's: green for
                    active, red for error. On a solid white chip, so it reads the same on all 41 brand
                    colours instead of depending on the one behind it.

                    The failure text is the chip's tooltip rather than a line of its own — "Error"
                    is the signal, and the detail is only wanted once you go looking.
                  */}
                  <span
                    title={connection.errorMessage || undefined}
                    className={cn(
                      // No dark: variants. The card behind it is a brand colour in both themes, so
                      // the pill is not sitting on the page background and must not follow it —
                      // theming it made the same connection look like two different states.
                      'inline-flex flex-none items-center gap-1.5 rounded-full bg-white px-2 py-0.5 text-[10.5px] font-bold shadow-sm',
                      !isEnabled
                        ? 'text-zinc-500'
                        : connection.status === 'error'
                          ? 'text-red-600'
                          : 'text-emerald-600',
                    )}
                  >
                    <span
                      className={cn(
                        'size-1.5 rounded-full',
                        !isEnabled
                          ? 'bg-zinc-400'
                          : connection.status === 'error'
                            ? 'bg-red-500'
                            : 'bg-emerald-500',
                      )}
                    />
                    {!isEnabled ? 'Paused' : connection.status === 'error' ? 'Error' : 'Active'}
                  </span>
                  {/* The ID is what you paste into code, so copying it is a one-click button here
                      rather than two clicks inside the menu. */}
                  <Button
                    size="sm"
                    variant="ghost"
                    title="Copy connection ID"
                    aria-label="Copy connection ID"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyId(connection.id);
                    }}
                    style={paint ? { color: fg } : undefined}
                    className="-mr-1 ml-auto h-6 w-6 flex-none p-0 hover:bg-black/10"
                  >
                    {copiedId === connection.id ? (
                      <Check className="size-3.5" />
                    ) : (
                      <Copy className="size-3.5" />
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
  };

  /**
   * A folder: one card standing for every connection of a service.
   *
   * It takes the footprint of a connection card and wears the same brand colour, so the row reads
   * as one kind of thing rather than two. What is inside is previewed by *name* — a stack of small
   * pills, each the same shape as the card it stands for. Connections of one service differ only by
   * the name you gave them, so the names are the only preview worth showing: a grid of identical
   * brand-coloured squares said "several of these" and nothing more.
   */
  const renderFolder = (type: string, items: Connection[]): React.ReactElement => {
    const connectorInfo = connectorsMap.get(type);
    const serviceName = connectorInfo?.name ?? type;
    const brand = connectorInfo?.brandTheme?.primary;
    const paused = items.filter((item) => item.enabled === false).length;
    // Same rule as a single card: nothing running means no brand wash, since that reads as healthy.
    const paint = paused < items.length ? brand : undefined;
    const fg = paint ? readableOn(paint, connectorInfo?.brandTheme?.primaryForeground) : undefined;
    const errored = items.filter(
      (item) => item.enabled !== false && item.status === 'error',
    ).length;

    // The last row counts the rest rather than being one more name, so the height is fixed.
    const overflow = items.length - FOLDER_PREVIEW_ROWS;
    const preview = overflow > 0 ? items.slice(0, FOLDER_PREVIEW_ROWS - 1) : items;

    return (
      <Card
        key={`folder-${type}`}
        data-testid="connection-folder"
        data-connector-id={type}
        style={paint ? { backgroundColor: paint, borderColor: paint, color: fg } : undefined}
        className={cn(CARD_SHELL, !paint && 'bg-muted/40')}
      >
        {/*
          The whole folder is the control — a folder has one action, which is to open it. The
          connections inside keep their own menus once it is open.
        */}
        <button
          type="button"
          onClick={() => setOpenFolderType(type)}
          aria-label={`${serviceName} — ${items.length} connections`}
          className="h-full w-full rounded-2xl text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <CardContent className={CARD_BODY}>
            <div className="flex items-center gap-2">
              <span className="grid size-9 flex-none place-items-center rounded-xl bg-white p-1.5 shadow-sm">
                <ConnectorIcon
                  connector={connectorInfo || { icon: 'Database', name: serviceName }}
                  className="size-full"
                  surface="light"
                />
              </span>
              <span className="min-w-0 flex-1 truncate text-[11.5px] font-semibold uppercase tracking-wide opacity-80">
                {serviceName}
              </span>
              <span className="flex-none rounded-full bg-white px-2 py-0.5 text-[10.5px] font-bold tabular-nums text-zinc-900 shadow-sm">
                {items.length}
              </span>
            </div>

            {/* The contained connections, each a smaller version of its own card. */}
            <div className="mt-3 space-y-1">
              {preview.map((item) => {
                const isEnabled = item.enabled !== false;
                const isErrored = isEnabled && item.status === 'error';
                return (
                  <span
                    key={item.id}
                    title={item.name}
                    className={cn(
                      'flex items-center gap-1.5 rounded-md bg-white/20 px-1.5 py-1',
                      isErrored && 'ring-1 ring-inset ring-red-400/80',
                      !paint && 'bg-muted',
                    )}
                  >
                    <span
                      className={cn(
                        'size-1.5 flex-none rounded-full bg-current',
                        !isEnabled && 'opacity-40',
                      )}
                    />
                    <span
                      className={cn(
                        'min-w-0 flex-1 truncate text-[11px] font-medium',
                        !isEnabled && 'opacity-60',
                      )}
                    >
                      {parseConnectionName(item.name).displayName}
                    </span>
                  </span>
                );
              })}
              {overflow > 0 && (
                <span className="block px-1.5 text-[11px] font-medium opacity-70">
                  +{overflow} more
                </span>
              )}
            </div>

            {/*
              Same position as a card's status pill, and the same job: one line of health. It reports
              the exceptions rather than the total, because "2 paused" is the part worth reading.
            */}
            <div className="mt-auto flex items-center gap-2 pt-3">
              <span
                className={cn(
                  'inline-flex flex-none items-center gap-1.5 rounded-full bg-white px-2 py-0.5 text-[10.5px] font-bold shadow-sm',
                  errored > 0 ? 'text-red-600' : paused > 0 ? 'text-zinc-500' : 'text-emerald-600',
                )}
              >
                <span
                  className={cn(
                    'size-1.5 rounded-full',
                    errored > 0 ? 'bg-red-500' : paused > 0 ? 'bg-zinc-400' : 'bg-emerald-500',
                  )}
                />
                {errored > 0
                  ? `${errored} failing`
                  : paused > 0
                    ? `${paused} paused`
                    : 'All active'}
              </span>
              <span className="ml-auto flex-none text-[11px] font-semibold opacity-80 group-hover:opacity-100">
                Open
              </span>
            </div>
          </CardContent>
        </button>
      </Card>
    );
  };

  // No header here: the Connectors page owns the "Your connections" heading, its count and its
  // search. Rendering one here too produced two titles stacked on top of each other.
  return (
    <div>
      {/*
        One row that scrolls sideways, never a wrapping grid. Connections are a strip you skim
        along the top of the page; letting them wrap meant the section grew downwards and pushed
        the source picker off the screen as soon as you had five or six of them.

        No visible scrollbar: a horizontal bar under a single row of cards reads as a piece of the
        component rather than chrome, and the partially visible card at the edge already says the
        row scrolls. `pb-1` stays for the cards' hover shadow, which the overflow would otherwise clip.
      */}
      <div className="scrollbar-hide flex gap-3 overflow-x-auto pb-1">
        {groups.map((group) =>
          group.items.length >= MIN_FOR_FOLDER
            ? renderFolder(group.type, group.items)
            : renderCard(group.items[0]),
        )}
      </div>

      {/*
        An opened folder. The contents are the same cards as the row, so every action on a
        connection is in one place whether or not it happens to be grouped.
      */}
      <Dialog
        open={!!openFolder}
        onOpenChange={(open) => !open && setOpenFolderType(null)}
      >
        <DialogContent
          data-testid="connection-folder-contents"
          className="max-h-[85vh] max-w-[calc(100vw-2rem)] overflow-y-auto"
          /*
            Sized to its contents, not to a breakpoint: exactly as many card columns as there are
            cards, up to three. A fixed max-w-2xl left a card-shaped hole beside a pair of them.
            `px-6` on the dialog is the 48px.
          */
          style={{
            width:
              folderColumns * CARD_WIDTH + (folderColumns - 1) * CARD_GAP + 48,
          }}
        >
          <DialogHeader>
            <div className="flex items-center gap-3">
              <span className="grid size-9 flex-none place-items-center rounded-xl bg-white p-1.5 shadow-sm ring-1 ring-border">
                <ConnectorIcon
                  connector={
                    (openFolder && connectorsMap.get(openFolder.type)) || {
                      icon: 'Database',
                      name: openFolder?.type ?? 'Unknown',
                    }
                  }
                  className="size-full"
                  surface="light"
                />
              </span>
              <div className="min-w-0">
                <DialogTitle className="truncate text-base">
                  {(openFolder && connectorsMap.get(openFolder.type)?.name) ??
                    openFolder?.type}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {openFolder?.items.length} connections to this service. Each one has its own
                  credentials and ID.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex flex-wrap gap-3">
            {openFolder?.items.map((connection) => renderCard(connection))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteConfirmId} onOpenChange={(open) => !open && setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Connection</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete{' '}
              <strong>{connectionToDelete?.name}</strong>? This action cannot be
              undone. Any projects using this connection will lose access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletingId ? 'Deleting...' : 'Delete Connection'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}